import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { Ban, Check, Clock, Infinity } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import useFetch from 'use-http';

import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function VisitsTable({ preselectedFutureVisits }) {
  const [data, setData] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestId = useRef(0);
  const userState = useUserState();
  const sessionDispatch = useUserDispatch();
  const [statusFilter, setStatusFilter] = useState('history');
  const {
    get: getVisits,
    abort,
  } = useFetch('/UserAccount/Visits');
  const [dateRange, setDateRange] = useState(userState.dateFilter);
  const history = useHistory();

  useEffect(() => {
    let isMounted = true;
    const requestId = ++latestRequestId.current;

    const fetchVisits = async () => {
      setIsLoading(true);

      const query = getQueryString(
        dateRange,
        '',
        statusFilter,
        undefined,
        userState.espbiEsi,
      );

      const response = await getVisits(
        `${query}${query ? '&' : '?'}includeCounts=true`,
      );

      if (!isMounted || requestId !== latestRequestId.current) return;

      if (Array.isArray(response?.visits)) {
        const mapped = response.visits.map((v) => ({
          id: v.id,
          serviceTitle: v.visitTitle,
          location: `${v.department}${
            v.departmentAddress ? ', ' + v.departmentAddress : ''
          }`,
          type: v.workplaceType,
          date: v.date,
          status: v.status,
          statusDisplay: v.statusDisplay,
          full: v,
        }));

        setData(mapped);
        setStatusCounts(response.counts || {});
      } else {
        setData([]);
        setStatusCounts({});
      }

      setIsLoading(false);
    };

    fetchVisits();

    return () => {
      isMounted = false;
      abort();
    };
  }, [statusFilter, userState?.espbiEsi, dateRange]);

  return (
    <DataTable
      title="Mano vizitai"
      loading={isLoading}
      columns={columns(history)}
      data={data}
      filterableColumns={['serviceTitle', 'status', 'date']}
      facetedFilters={[
        {
          columnId: 'status',
          title: 'Statusas',
          options: [
            {
              value: 'history',
              label: 'Visi',
              icon: Infinity,
              count: statusCounts.history,
            },
            {
              value: 'registrations',
              label: 'Būsimi',
              icon: Clock,
              count: statusCounts.registrations,
            },
            {
              value: 'done',
              label: 'Įvykę',
              icon: Check,
              count: statusCounts.done,
            },
            {
              value: 'failed',
              label: 'Neįvykę',
              icon: Ban,
              count: statusCounts.failed,
            },
          ],
        },
      ]}
      onStatusFilterChange={(status) => setStatusFilter(status)}
      preselectedFutureVisits={preselectedFutureVisits}
      onRowClick={(row) =>
        history.push('/userAccount/visits/details', { visit: row.full })
      }
    />
  );
}

const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatDateOnlyWithoutConversion = (date) => {
  if (!date) {
    return '-';
  }

  return String(date).substring(0, 10);
};

const columns = (history) => [
  {
    accessorKey: 'serviceTitle',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PASLAUGA/GYDYTOJAS" />
    ),
    cell: ({ row }) => (
      <div className="font-medium leading-tight ">
        <div>{capitalizeFirstLetter(row.original.serviceTitle)}</div>
        <div className="text-muted-foreground text-sm ">
          {row.original.location}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="TIPAS" />
    ),
    cell: ({ row }) => row.original.type || '-',
  },
  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {formatDateOnlyWithoutConversion(row.original.date)}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="STATUSAS" />
    ),
    cell: ({ row }) => {
      const display = row.original.statusDisplay;
      let variant = 'neutral';

      if (display === 'Įvykusi') variant = 'success';
      else if (display === 'Neįvykęs') variant = 'fail';

      return <Badge variant={variant}>{display}</Badge>;
    },
    enableSorting: true,
    enableFiltering: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.statusDisplay || '';
      const b = rowB.original.statusDisplay || '';

      return a.localeCompare(b, 'lt');
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <InfoButton
        onClick={(e) => {
          e.stopPropagation();

          history.push('/userAccount/visits/details', {
            visit: row.original.full,
          });
        }}
      >
        Vizito informacija
      </InfoButton>
    ),
  },
];
